(() => {
  const controllers = new WeakMap();
  const productStates = new Map();

  const money = (cents, format) => {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
    return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR',
    }).format((Number(cents) || 0) / 100);
  };

  const findSection = (node) => node?.matches?.('[data-cloudless-controller]')
    ? node
    : node?.querySelector?.('[data-cloudless-controller]');

  class Controller {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.cleanups = [];
      this.init();
    }

    on(target, event, listener, options = {}) {
      target?.addEventListener(event, listener, { ...options, signal: this.abort.signal });
    }

    cleanup(callback) { this.cleanups.push(callback); }

    destroy() {
      this.abort.abort();
      this.cleanups.splice(0).forEach((callback) => callback());
      controllers.delete(this.root);
    }

    init() {
      const names = (this.root.dataset.cloudlessController || '').split(/\s+/).filter(Boolean);
      names.forEach((name) => this[name]?.());
    }

    product() {
      const form = this.root.querySelector('.cloudless-product-form, [data-cloudless-product-form]');
      const idInput = form?.querySelector('[name="id"]');
      const quantityInput = form?.querySelector('[name="quantity"]');
      const sellingPlanInput = form?.querySelector('[name="selling_plan"]');
      const routineWidget = this.root.querySelector('[data-cloudless-routine-widget]');
      const routineToggle = routineWidget?.querySelector('[data-cloudless-routine-toggle]');
      const addToCartContainer = form?.querySelector('add-to-cart-component');
      const addToCartButton = addToCartContainer?.querySelector('[ref="addToCartButton"]') || form?.querySelector('[type="submit"]');
      const offers = [...this.root.querySelectorAll('[data-cloudless-offer]')];
      const priceNodes = [...this.root.querySelectorAll('[data-cloudless-price]')];
      const compareNodes = [...this.root.querySelectorAll('[data-cloudless-compare-price]')];
      const gallery = this.root.querySelector('[data-cloudless-gallery]');
      const galleryItems = [...this.root.querySelectorAll('[data-cloudless-gallery-item]')];
      const galleryMarkers = [...this.root.querySelectorAll('[data-cloudless-gallery-marker]')];
      const productKey = String(this.root.dataset.productId || 'product');
      let galleryFrame = 0;
      let activeVariantGalleryItem = null;
      let gallerySyncFrame = 0;
      let syncGalleryForVariant = () => {};
      let variants = [];

      try {
        variants = JSON.parse(this.root.querySelector('[data-cloudless-variants]')?.textContent || '[]');
      } catch (_) {
        variants = [];
      }

      const getVariant = (offer) => {
        const exact = offer?.dataset.variantId;
        const match = (offer?.dataset.variantMatch || '').trim().toLowerCase();
        return variants.find((variant) => exact && String(variant.id) === String(exact))
          || variants.find((variant) => match && String(variant.title || '').toLowerCase().includes(match))
          || variants.find((variant) => String(variant.id) === String(idInput?.value))
          || variants.find((variant) => variant.available)
          || variants[0];
      };

      const offerForVariant = (variant) => {
        if (!variant) return null;
        const variantId = String(variant.id || '');
        const variantTitle = String(variant.title || '').trim().toLowerCase();
        return offers.find((offer) => offer.dataset.variantId && String(offer.dataset.variantId) === variantId)
          || offers.find((offer) => {
            const match = (offer.dataset.variantMatch || '').trim().toLowerCase();
            return match && variantTitle.includes(match);
          })
          || null;
      };

      const allocationFor = (variant, planId) => {
        const allocations = variant?.selling_plan_allocations || [];
        return allocations.find((allocation) => String(allocation.selling_plan_id) === String(planId)) || null;
      };

      const syncPlanOptions = (variant) => {
        const planSelect = this.root.querySelector('[data-cloudless-selling-plans]');
        const subscription = this.root.querySelector('[data-cloudless-subscription]');
        if (!planSelect || !subscription?.checked) {
          if (routineToggle && subscription && !subscription.checked) routineToggle.checked = false;
          return null;
        }
        const allocations = variant?.selling_plan_allocations || [];
        const availablePlanIds = new Set(allocations.map((allocation) => String(allocation.selling_plan_id)));
        [...planSelect.options].forEach((option) => { option.disabled = !availablePlanIds.has(String(option.value)); });
        let allocation = allocationFor(variant, planSelect.value);
        if (!allocation) {
          const firstOption = [...planSelect.options].find((option) => !option.disabled);
          if (firstOption) {
            planSelect.value = firstOption.value;
            allocation = allocationFor(variant, firstOption.value);
          }
        }
        if (!allocation) {
          subscription.checked = false;
          const oneTime = this.root.querySelector('[name^="cloudless-purchase-"][value="one-time"]');
          if (oneTime) oneTime.checked = true;
          if (routineToggle) routineToggle.checked = false;
        }
        if (routineToggle) routineToggle.checked = Boolean(allocation);
        return allocation;
      };

      const setButtonState = (available) => {
        if (available) addToCartContainer?.enable?.();
        else addToCartContainer?.disable?.();
        if (!addToCartButton) return;
        addToCartButton.disabled = !available;
        const label = addToCartButton.querySelector('[data-cloudless-atc-label]');
        if (label) label.textContent = available
          ? (this.root.dataset.availableLabel || 'In winkelwagen')
          : (this.root.dataset.soldOutLabel || 'Uitverkocht');
      };

      const hydrateOfferPrices = () => {
        const format = this.root.dataset.moneyFormat;
        offers.forEach((offer) => {
          if (offer.dataset.useVariantPrice !== 'true') return;
          const variant = getVariant(offer);
          if (!variant) return;
          const price = Number(variant.price) || 0;
          const comparePrice = Number(variant.compare_at_price) || 0;
          const priceNode = offer.querySelector('[data-cloudless-offer-price]');
          const compareNode = offer.querySelector('[data-cloudless-offer-compare-price]');
          const dayPriceNode = offer.querySelector('[data-cloudless-offer-day-price]');
          const daysSupply = Math.max(1, Number(offer.dataset.daysSupply) || 30);
          if (priceNode) priceNode.textContent = money(price, format);
          if (compareNode) {
            compareNode.textContent = comparePrice > price ? money(comparePrice, format) : '';
            compareNode.hidden = !(comparePrice > price);
          }
          if (dayPriceNode) dayPriceNode.textContent = offer.dataset.dailyPriceLabel || `Slechts ${money(Math.floor(price / daysSupply), format)} per dag`;
        });
      };

      const update = (offer, preferredVariant = null, { navigateGallery = true } = {}) => {
        const variant = preferredVariant || getVariant(offer);
        if (!variant) return;
        const minimumQuantity = Number(variant.quantity_rule?.min) || 1;
        const inventoryQuantity = Number(variant.inventory_quantity);
        const quantityRuleSoldOut = variant.inventory_management === 'shopify'
          && variant.inventory_policy === 'deny'
          && Number.isFinite(inventoryQuantity)
          && minimumQuantity > inventoryQuantity;
        const available = Boolean(variant.available) && !quantityRuleSoldOut;

        offers.forEach((item) => {
          const selected = item === offer;
          item.classList.toggle('is-selected', selected);
          const radio = item.querySelector('input[type="radio"]');
          if (radio) {
            radio.checked = selected;
            radio.setAttribute('aria-checked', String(selected));
          }
        });

        if (idInput) idInput.value = variant.id;
        if (quantityInput) quantityInput.value = minimumQuantity;
        const productFormComponent = form?.closest('product-form-component');
        if (productFormComponent) productFormComponent.dataset.quantityDefault = String(minimumQuantity);
        const allocation = syncPlanOptions(variant);
        if (sellingPlanInput) sellingPlanInput.value = allocation?.selling_plan_id || '';

        const finalPrice = Number(allocation?.price ?? variant.price) || 0;
        const comparePrice = Number(allocation?.compare_at_price ?? variant.compare_at_price) || 0;
        const format = this.root.dataset.moneyFormat;
        const formattedPrice = money(finalPrice, format);
        const formattedComparePrice = comparePrice > finalPrice ? money(comparePrice, format) : '';
        priceNodes.forEach((node) => { node.textContent = formattedPrice; });
        compareNodes.forEach((node) => {
          node.textContent = formattedComparePrice;
          node.hidden = !formattedComparePrice;
        });

        setButtonState(available);
        const variantImage = variant.featured_image?.src
          || variant.featured_media?.preview_image?.src
          || '';
        if (addToCartContainer && variantImage) {
          addToCartContainer.dataset.productVariantMedia = `${variantImage}${variantImage.includes('?') ? '&' : '?'}width=100`;
        }
        syncGalleryForVariant(variant, offer, navigateGallery);

        const state = {
          productId: productKey,
          productTitle: this.root.dataset.productTitle || '',
          variant,
          offer: offer ? {
            title: offer.dataset.offerTitle || '',
            subline: offer.dataset.offerSubline || '',
            image: offer.dataset.offerImage || '',
            imageAlt: offer.dataset.offerImageAlt || '',
          } : null,
          price: finalPrice,
          comparePrice,
          formattedPrice,
          formattedComparePrice,
          available,
          variantImage,
          sellingPlanId: allocation?.selling_plan_id || '',
        };
        productStates.set(productKey, state);
        document.dispatchEvent(new CustomEvent('cloudless:product-state', { detail: state }));

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        this.root.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: state,
        }));
      };

      const selectedOffer = () => this.root.querySelector('[data-cloudless-offer] input:checked')?.closest('[data-cloudless-offer]') || offers[0] || null;

      offers.forEach((offer) => {
        this.on(offer.querySelector('input'), 'change', () => update(offer));
      });

      this.root.querySelectorAll('[name^="cloudless-purchase-"]').forEach((input) => {
        this.on(input, 'change', () => update(selectedOffer()));
      });
      this.on(this.root.querySelector('[data-cloudless-selling-plans]'), 'change', () => update(selectedOffer()));
      this.on(routineToggle, 'change', () => {
        const subscription = this.root.querySelector('[data-cloudless-subscription]');
        const oneTime = this.root.querySelector('[name^="cloudless-purchase-"][value="one-time"]');
        if (routineToggle.checked && subscription) subscription.checked = true;
        else if (oneTime) oneTime.checked = true;
        routineWidget?.classList.toggle('is-selected', routineToggle.checked);
        update(selectedOffer());
      });

      const setCurrentMarker = (index) => {
        galleryMarkers.forEach((marker) => marker.setAttribute('aria-current', String(marker.dataset.cloudlessGalleryMarker === String(index))));
      };
      const selectGalleryItem = (index, behavior = 'smooth') => {
        const item = galleryItems.find((candidate) => candidate.dataset.cloudlessMediaIndex === String(index));
        if (!gallery || !item || item.hidden) return;
        const left = item.offsetLeft - gallery.offsetLeft;
        gallery.scrollTo({ left, behavior });
        setCurrentMarker(index);
      };
      const normalizedMediaId = (value) => String(value || '').split('/').pop();
      const normalizedMatch = (value) => String(value || '')
        .trim()
        .toLocaleLowerCase(document.documentElement.lang || undefined)
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim();
      const markerForItem = (item) => galleryMarkers.find((marker) => (
        marker.dataset.cloudlessGalleryMarker === item?.dataset.cloudlessMediaIndex
      ));
      const semanticGalleryItem = (variant, offer, filteredItems) => {
        const wanted = [offer?.dataset.variantMatch, variant?.title, offer?.dataset.offerTitle]
          .map(normalizedMatch)
          .filter(Boolean);
        const exact = filteredItems.find((item) => {
          const marker = markerForItem(item);
          const available = [
            item.dataset.variantMatch,
            item.dataset.packLabel,
            item.querySelector('img')?.alt,
            marker?.getAttribute('aria-label'),
          ].map(normalizedMatch).filter(Boolean);
          return wanted.some((candidate) => available.includes(candidate));
        });
        if (exact) return exact;

        const contained = filteredItems.find((item) => {
          const marker = markerForItem(item);
          const available = [
            item.dataset.variantMatch,
            item.dataset.packLabel,
            item.querySelector('img')?.alt,
            marker?.getAttribute('aria-label'),
          ].map(normalizedMatch).filter(Boolean);
          return wanted.some((candidate) => available.some((value) => (
            value.includes(candidate) || candidate.includes(value)
          )));
        });
        if (contained) return contained;

        const quantity = Number.parseInt(wanted.map((value) => value.match(/\d+/)?.[0]).find(Boolean) || '', 10);
        if (Number.isFinite(quantity) && quantity > 0) {
          const quantityMatch = filteredItems.find((item) => {
            const marker = markerForItem(item);
            const available = [
              item.dataset.variantMatch,
              item.dataset.packLabel,
              item.querySelector('img')?.alt,
              item.querySelector('img')?.currentSrc,
              item.querySelector('img')?.src,
              marker?.getAttribute('aria-label'),
            ].map(normalizedMatch).filter(Boolean);
            return available.some((value) => value.match(/\d+/)?.some((part) => Number(part) === quantity));
          });
          if (quantityMatch) return quantityMatch;
        }

        const offerIndex = offers.indexOf(offer);
        return offerIndex >= 0 ? filteredItems[offerIndex] || null : null;
      };
      syncGalleryForVariant = (variant, offer, navigate = true) => {
        if (!gallery || !galleryItems.length) return;
        const filteredItems = galleryItems.filter((item) => item.dataset.cloudlessFiltered === 'true');

        const featuredMediaId = normalizedMediaId(
          variant?.featured_media?.id || variant?.featured_image?.id || variant?.featured_media_id,
        );
        const featuredItem = featuredMediaId
          ? galleryItems.find((item) => normalizedMediaId(item.dataset.mediaId) === featuredMediaId)
          : null;
        const target = featuredItem || (filteredItems.length ? semanticGalleryItem(variant, offer, filteredItems) : null);
        const selectedFilteredItem = target?.dataset.cloudlessFiltered === 'true' ? target : null;

        filteredItems.forEach((item) => {
          const selected = item === selectedFilteredItem;
          const marker = markerForItem(item);
          item.classList.toggle('is-variant-active', selected);
          if (selected) {
            item.hidden = false;
            item.dataset.cloudlessVariantReveal = 'true';
          } else {
            delete item.dataset.cloudlessVariantReveal;
            if (item.dataset.cloudlessEditorReveal !== 'true') item.hidden = true;
          }
          if (!marker) return;
          if (selected) {
            marker.hidden = false;
            marker.dataset.cloudlessVariantReveal = 'true';
          } else {
            delete marker.dataset.cloudlessVariantReveal;
            if (marker.dataset.cloudlessEditorReveal !== 'true') marker.hidden = true;
          }
        });

        if (!target || target === activeVariantGalleryItem || !navigate) return;
        activeVariantGalleryItem = target;
        cancelAnimationFrame(gallerySyncFrame);
        gallerySyncFrame = requestAnimationFrame(() => {
          const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          selectGalleryItem(target.dataset.cloudlessMediaIndex, behavior);
        });
      };
      galleryMarkers.forEach((marker) => {
        this.on(marker, 'click', () => selectGalleryItem(marker.dataset.cloudlessGalleryMarker));
      });
      const renderGalleryMarker = () => {
        galleryFrame = 0;
        if (!gallery || gallery.scrollWidth <= gallery.clientWidth) return;
        const left = gallery.scrollLeft + 1;
        const visible = galleryItems.filter((item) => !item.hidden);
        const current = visible.reduce((closest, item) => {
          if (!closest) return item;
          return Math.abs(item.offsetLeft - left) < Math.abs(closest.offsetLeft - left) ? item : closest;
        }, null);
        if (current) setCurrentMarker(current.dataset.cloudlessMediaIndex);
      };
      this.on(gallery, 'scroll', () => {
        if (!galleryFrame) galleryFrame = requestAnimationFrame(renderGalleryMarker);
      }, { passive: true });
      this.on(document, 'shopify:block:select', (event) => {
        const item = event.target.closest?.('[data-cloudless-gallery-item]');
        if (!item || !this.root.contains(item)) return;
        if (item.dataset.cloudlessFiltered === 'true') {
          item.hidden = false;
          item.dataset.cloudlessEditorReveal = 'true';
          const marker = galleryMarkers.find((candidate) => candidate.dataset.cloudlessBlockId === item.dataset.cloudlessBlockId);
          if (marker) {
            marker.hidden = false;
            marker.dataset.cloudlessEditorReveal = 'true';
          }
        }
        selectGalleryItem(item.dataset.cloudlessMediaIndex, 'auto');
      });
      this.on(document, 'shopify:block:deselect', (event) => {
        const item = event.target.closest?.('[data-cloudless-gallery-item]');
        if (!item || !this.root.contains(item) || item.dataset.cloudlessEditorReveal !== 'true') return;
        delete item.dataset.cloudlessEditorReveal;
        if (item.dataset.cloudlessVariantReveal !== 'true') item.hidden = true;
        const marker = galleryMarkers.find((candidate) => candidate.dataset.cloudlessBlockId === item.dataset.cloudlessBlockId);
        if (marker?.dataset.cloudlessEditorReveal === 'true') {
          delete marker.dataset.cloudlessEditorReveal;
          if (marker.dataset.cloudlessVariantReveal !== 'true') marker.hidden = true;
        }
      });
      this.cleanup(() => {
        cancelAnimationFrame(galleryFrame);
        cancelAnimationFrame(gallerySyncFrame);
      });

      hydrateOfferPrices();
      const initialVariant = variants.find((variant) => String(variant.id) === String(idInput?.value));
      const initialOffer = offerForVariant(initialVariant);
      update(initialOffer, initialVariant || null, { navigateGallery: false });

      const timer = this.root.querySelector('[data-cloudless-timer]');
      if (timer) {
        const endValue = timer.dataset.end;
        const midnight = timer.dataset.mode === 'midnight';
        const tick = () => {
          const now = new Date();
          let end = endValue ? new Date(endValue) : null;
          if (midnight || !end || Number.isNaN(end.getTime())) {
            end = new Date(now);
            end.setHours(24, 0, 0, 0);
          }
          const distance = Math.max(0, end - now);
          const hours = Math.floor(distance / 3_600_000);
          const minutes = Math.floor((distance % 3_600_000) / 60_000);
          const seconds = Math.floor((distance % 60_000) / 1000);
          timer.textContent = [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':');
        };
        tick();
        const timerId = window.setInterval(tick, 1000);
        this.cleanup(() => window.clearInterval(timerId));
      }

      const lightbox = this.root.querySelector('[data-cloudless-lightbox]');
      const lightboxImage = lightbox?.querySelector('img');
      let lightboxTrigger = null;
      const closeLightbox = () => {
        lightbox?.classList.remove('is-open');
        lightbox?.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('lock');
        lightboxTrigger?.focus?.();
        lightboxTrigger = null;
      };
      this.root.querySelectorAll('[data-cloudless-zoom]').forEach((button) => {
        this.on(button, 'click', () => {
          if (!lightbox || !lightboxImage) return;
          lightboxTrigger = button;
          lightboxImage.src = button.dataset.cloudlessZoom;
          lightboxImage.alt = button.dataset.alt || '';
          lightbox.classList.add('is-open');
          lightbox.setAttribute('aria-hidden', 'false');
          document.documentElement.classList.add('lock');
          lightbox.querySelector('button')?.focus();
        });
      });
      this.on(lightbox?.querySelector('[data-cloudless-lightbox-close]'), 'click', closeLightbox);
      this.on(lightbox, 'click', (event) => { if (event.target === lightbox) closeLightbox(); });
      this.on(document, 'keydown', (event) => {
        if (!lightbox?.classList.contains('is-open')) return;
        if (event.key === 'Escape') closeLightbox();
        if (event.key !== 'Tab') return;
        const focusable = [...lightbox.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      const videoTriggers = new WeakMap();
      this.root.querySelectorAll('[data-cloudless-video-open]').forEach((button) => {
        this.on(button, 'click', () => {
          const dialog = this.root.querySelector(`#${CSS.escape(button.dataset.cloudlessVideoOpen)}`);
          if (!dialog) return;
          videoTriggers.set(dialog, button);
          dialog.showModal();
          const video = dialog.querySelector('video');
          if (video) {
            video.currentTime = 0;
            video.play().catch(() => {});
          }
        });
      });
      this.root.querySelectorAll('[data-cloudless-video-dialog]').forEach((dialog) => {
        const pause = () => dialog.querySelector('video')?.pause();
        const close = () => {
          pause();
          if (dialog.open) dialog.close();
        };
        this.on(dialog.querySelector('[data-cloudless-video-close]'), 'click', close);
        this.on(dialog, 'click', (event) => { if (event.target === dialog) close(); });
        this.on(dialog, 'cancel', (event) => {
          event.preventDefault();
          close();
        });
        this.on(dialog, 'close', () => {
          pause();
          videoTriggers.get(dialog)?.focus?.();
        });
      });

      const sheetTriggers = new WeakMap();
      this.root.querySelectorAll('[data-cloudless-sheet-open]').forEach((button) => {
        this.on(button, 'click', () => {
          const id = button.dataset.cloudlessSheetOpen;
          const sheet = this.root.querySelector(`#${CSS.escape(id)}`);
          const overlay = this.root.querySelector(`[data-cloudless-sheet-overlay="${CSS.escape(id)}"]`);
          if (!sheet) return;
          sheetTriggers.set(sheet, button);
          sheet.inert = false;
          sheet.classList.add('active');
          sheet.setAttribute('aria-hidden', 'false');
          overlay?.classList.add('active');
          overlay?.setAttribute('aria-hidden', 'false');
          document.documentElement.classList.add('lock');
          (sheet.querySelector('[data-cloudless-sheet-close]') || sheet).focus();
        });
      });
      this.root.querySelectorAll('[data-cloudless-sheet]').forEach((sheet) => {
        const overlay = this.root.querySelector(`[data-cloudless-sheet-overlay="${CSS.escape(sheet.id)}"]`);
        const close = (returnFocus = true) => {
          sheet.classList.remove('active');
          sheet.inert = true;
          sheet.setAttribute('aria-hidden', 'true');
          overlay?.classList.remove('active');
          overlay?.setAttribute('aria-hidden', 'true');
          document.documentElement.classList.remove('lock');
          if (returnFocus) sheetTriggers.get(sheet)?.focus?.();
        };
        this.on(sheet.querySelector('[data-cloudless-sheet-close]'), 'click', () => close());
        this.on(overlay, 'click', () => close());
        this.on(document, 'keydown', (event) => {
          if (!sheet.classList.contains('active')) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
          }
          if (event.key !== 'Tab') return;
          const focusable = [...sheet.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
          if (!focusable.length) {
            event.preventDefault();
            sheet.focus();
            return;
          }
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        });
        this.cleanup(() => {
          if (sheet.classList.contains('active')) close(false);
        });
      });
    }

    sticky() {
      const safeQuery = (selector, fallback) => {
        try {
          return document.querySelector(selector || fallback);
        } catch (_error) {
          return document.querySelector(fallback);
        }
      };
      const productForm = safeQuery(this.root.dataset.triggerSelector, '.cloudless-product-form, [data-cloudless-product-form]');
      const footer = this.root.dataset.hideNearFooter === 'true'
        ? safeQuery(this.root.dataset.footerSelector, '.cloudless-footer, footer')
        : null;
      const showOffset = Math.max(0, Number(this.root.dataset.showOffset || 0));
      const button = this.root.querySelector('[data-cloudless-sticky-button]');
      const titleNode = this.root.querySelector('[data-cloudless-sticky-title]');
      const subheadingNode = this.root.querySelector('[data-cloudless-sticky-subheading]');
      const imageNode = this.root.querySelector('[data-cloudless-sticky-image]');
      const priceNode = this.root.querySelector('[data-cloudless-price]');
      const compareNode = this.root.querySelector('[data-cloudless-compare-price]');
      const buttonLabel = this.root.querySelector('[data-cloudless-sticky-button-label]');
      const productKey = String(this.root.dataset.productId || 'product');
      let formAbove = false;
      let footerVisible = false;
      let contrastFrame = 0;
      const applyProductState = (state) => {
        if (!state || String(state.productId) !== productKey) return;
        const baseTitle = this.root.dataset.baseTitle || state.productTitle || '';
        const variantTitle = state.variant?.title && state.variant.title !== 'Default Title' ? state.variant.title : '';
        const offerTitle = state.offer?.title || '';
        const titleSource = this.root.dataset.titleSource || 'product';
        let title = baseTitle;
        if (titleSource === 'offer') title = offerTitle || variantTitle || baseTitle;
        if (titleSource === 'variant') title = variantTitle || offerTitle || baseTitle;
        if (titleSource === 'product_offer') title = offerTitle ? `${baseTitle} · ${offerTitle}` : baseTitle;
        if (titleNode) titleNode.textContent = title;

        const subheadingSource = this.root.dataset.subheadingSource || 'custom';
        let subheading = this.root.dataset.baseSubheading || '';
        if (subheadingSource === 'offer') subheading = state.offer?.subline || offerTitle || subheading;
        if (subheadingSource === 'variant') subheading = variantTitle || subheading;
        if (subheadingNode) {
          subheadingNode.textContent = subheading;
          subheadingNode.hidden = !subheading;
        }

        const imageSource = this.root.dataset.imageSource || 'offer';
        if (imageNode && imageSource !== 'fixed') {
          const image = imageSource === 'variant'
            ? (state.variantImage || state.offer?.image)
            : (state.offer?.image || state.variantImage);
          if (image) imageNode.src = image;
          imageNode.alt = state.offer?.imageAlt || title;
        }

        if (priceNode) priceNode.textContent = state.formattedPrice || money(state.price, this.root.dataset.moneyFormat);
        if (compareNode) {
          compareNode.textContent = state.formattedComparePrice || '';
          compareNode.hidden = !state.formattedComparePrice;
        }
        if (button) {
          button.disabled = !state.available;
          const label = state.available
            ? (button.dataset.availableLabel || 'Start nu')
            : (button.dataset.soldOutLabel || 'Uitverkocht');
          if (buttonLabel) buttonLabel.textContent = label;
          button.setAttribute('aria-label', label);
        }
      };
      const luminance = (red, green, blue) => {
        const [r, g, b] = [red, green, blue].map((value) => {
          const channel = value / 255;
          return channel <= .03928 ? channel / 12.92 : ((channel + .055) / 1.055) ** 2.4;
        });
        return .2126 * r + .7152 * g + .0722 * b;
      };
      const parseColor = (value) => {
        const match = value.match(/rgba?\(\s*(\d+)\D+(\d+)\D+(\d+)(?:\D+([\d.]+))?/i);
        return match ? { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]), a: match[4] == null ? 1 : Number(match[4]) } : null;
      };
      const effectiveBackground = (element) => {
        let current = element;
        while (current && current !== document.documentElement) {
          const color = parseColor(getComputedStyle(current).backgroundColor);
          if (color && color.a > .1) return color;
          current = current.parentElement;
        }
        return { r: 255, g: 255, b: 255 };
      };
      const updateContrast = () => {
        contrastFrame = 0;
        if (!this.root.classList.contains('is-visible')) return;
        const rect = this.root.getBoundingClientRect();
        const points = [.2, .5, .8].map((ratio) => ({ x: rect.left + rect.width * ratio, y: rect.top + rect.height / 2 }));
        const previousVisibility = this.root.style.visibility;
        this.root.style.visibility = 'hidden';
        const dark = points.filter(({ x, y }) => {
          const behind = document.elementFromPoint(x, y);
          const color = effectiveBackground(behind);
          return luminance(color.r, color.g, color.b) < .45;
        }).length >= 2;
        this.root.style.visibility = previousVisibility;
        this.root.classList.toggle('is-dark', dark);
      };
      const queueContrast = () => {
        if (!contrastFrame) contrastFrame = requestAnimationFrame(updateContrast);
      };
      const render = () => {
        const visible = formAbove && !footerVisible;
        this.root.classList.toggle('is-visible', visible);
        if (visible) queueContrast();
      };

      if (productForm) {
        const observer = new IntersectionObserver(([entry]) => {
          formAbove = !entry.isIntersecting && entry.boundingClientRect.bottom < -showOffset;
          render();
        }, { rootMargin: `${showOffset}px 0px 0px 0px` });
        observer.observe(productForm);
        this.cleanup(() => observer.disconnect());
      }
      if (footer) {
        const observer = new IntersectionObserver(([entry]) => {
          footerVisible = entry.isIntersecting;
          render();
        });
        observer.observe(footer);
        this.cleanup(() => observer.disconnect());
      }
      this.on(document, 'cloudless:product-state', (event) => applyProductState(event.detail));
      applyProductState(productStates.get(productKey));
      this.on(button, 'click', () => {
        const target = safeQuery(
          this.root.dataset.scrollTargetSelector,
          '.cloudless-offer-picker, .cloudless-product-form, [data-cloudless-product-form], product-form',
        );
        const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        target?.scrollIntoView({ behavior, block: 'center' });
      });
      this.on(window, 'scroll', queueContrast, { passive: true });
      this.on(window, 'resize', queueContrast, { passive: true });
      this.cleanup(() => cancelAnimationFrame(contrastFrame));
    }

    capsule() {
      const capsules = [...this.root.querySelectorAll('[data-parallax-speed]')];
      const fill = this.root.querySelector('[data-scroll-fill]');
      const parallaxEnabled = this.root.dataset.parallaxEnabled === 'true';
      const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
      let frame = 0;
      const render = () => {
        frame = 0;
        const rect = this.root.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
        capsules.forEach((capsule) => {
          const speed = Number(capsule.dataset.parallaxSpeed || 0);
          const offset = parallaxEnabled && !reduceMotion.matches ? (progress - .5) * 150 * speed : 0;
          capsule.style.transform = `translate3d(0, ${offset}px, 0)`;
        });
        if (fill) fill.style.setProperty('--fill-progress', Math.min(1, Math.max(0, (progress - .2) / .4)));
      };
      const queue = () => { if (!frame) frame = requestAnimationFrame(render); };
      this.on(window, 'scroll', queue, { passive: true });
      this.on(window, 'resize', queue, { passive: true });
      this.on(reduceMotion, 'change', queue);
      queue();
      this.cleanup(() => cancelAnimationFrame(frame));
    }

    editor() {
      this.on(document, 'shopify:block:select', (event) => {
        const block = event.target.closest?.('[data-cloudless-editor-block]');
        if (!block || !this.root.contains(block)) return;
        block.scrollIntoView({
          behavior: event.detail?.load ? 'auto' : 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      });
    }

    slider() {
      const track = this.root.querySelector('[data-cloudless-slider-track]');
      if (!track) return;
      const previous = this.root.querySelector('[data-cloudless-prev]');
      const next = this.root.querySelector('[data-cloudless-next]');
      const desktop = matchMedia('(min-width: 768px)');
      let resizeTimer = 0;
      let wasDesktop = desktop.matches;
      const setControlState = (button, disabled) => {
        if (!button) return;
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
        if (disabled) button.setAttribute('tabindex', '-1');
        else button.removeAttribute('tabindex');
      };
      const renderControls = () => {
        if (!desktop.matches) {
          setControlState(previous, true);
          setControlState(next, true);
          return;
        }
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        setControlState(previous, track.scrollLeft <= 1);
        setControlState(next, max <= 1 || track.scrollLeft >= max - 1);
      };
      const move = (direction) => {
        if (!desktop.matches) return;
        const card = track.querySelector('[data-cloudless-slide]');
        const gap = Number.parseFloat(getComputedStyle(track).gap || '0');
        track.scrollBy({ left: direction * ((card?.getBoundingClientRect().width || track.clientWidth) + gap), behavior: 'smooth' });
      };
      this.on(previous, 'click', () => move(-1));
      this.on(next, 'click', () => move(1));
      this.on(track, 'scroll', renderControls, { passive: true });
      this.on(window, 'resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
          if (wasDesktop !== desktop.matches) {
            track.scrollTo({ left: 0, behavior: 'auto' });
            wasDesktop = desktop.matches;
          }
          renderControls();
        }, 100);
      }, { passive: true });
      this.on(document, 'shopify:block:select', (event) => {
        const slide = event.target.closest?.('[data-cloudless-slide]');
        if (!slide || !this.root.contains(slide)) return;
        slide.scrollIntoView({ behavior: event.detail?.load ? 'auto' : 'smooth', inline: desktop.matches ? 'center' : 'nearest', block: 'center' });
      });
      renderControls();
      this.cleanup(() => window.clearTimeout(resizeTimer));
    }

    testimonials() {
      const tracks = [...this.root.querySelectorAll('[data-cloudless-testimonial-track]')];
      const desktopTracks = tracks.filter((track) => track.dataset.cloudlessTestimonialMode === 'desktop');
      const mobileTracks = tracks.filter((track) => track.dataset.cloudlessTestimonialMode === 'mobile');
      const desktopNav = this.root.querySelector('[data-cloudless-testimonial-nav="desktop"]');
      const mobileNav = this.root.querySelector('[data-cloudless-testimonial-nav="mobile"]');
      const mobileView = matchMedia('(max-width: 991px)');
      let syncFrame = 0;
      let releaseSyncTimer = 0;
      let syncSource = null;
      let resizeTimer = 0;
      const cards = (track) => [...track.querySelectorAll('[data-cloudless-slide]')];
      const maxScroll = (track) => Math.max(0, track.scrollWidth - track.clientWidth);
      const nearestIndex = (track) => {
        const items = cards(track);
        if (!items.length) return 0;
        const viewportCenter = track.scrollLeft + track.clientWidth / 2;
        let nearest = 0;
        let distance = Number.POSITIVE_INFINITY;
        items.forEach((item, index) => {
          const itemCenter = item.offsetLeft + item.offsetWidth / 2;
          const itemDistance = Math.abs(viewportCenter - itemCenter);
          if (itemDistance < distance) {
            distance = itemDistance;
            nearest = index;
          }
        });
        return nearest;
      };
      const nearestStartIndex = (track) => {
        const items = cards(track);
        if (!items.length) return 0;
        let nearest = 0;
        let distance = Number.POSITIVE_INFINITY;
        items.forEach((item, index) => {
          const itemDistance = Math.abs(item.offsetLeft - track.scrollLeft);
          if (itemDistance < distance) {
            distance = itemDistance;
            nearest = index;
          }
        });
        return nearest;
      };
      const scrollToIndex = (track, index, centered, behavior = 'smooth') => {
        const items = cards(track);
        if (!items.length) return;
        const normalized = ((index % items.length) + items.length) % items.length;
        const item = items[normalized];
        const left = centered
          ? item.offsetLeft - (track.clientWidth - item.offsetWidth) / 2
          : item.offsetLeft;
        track.scrollTo({ left: Math.max(0, Math.min(maxScroll(track), left)), behavior });
      };
      const scrollDesktopToIndex = (track, index, behavior = 'auto') => {
        const items = cards(track);
        if (!items.length) return;
        const bounded = Math.max(0, Math.min(items.length - 1, index));
        track.scrollTo({ left: Math.max(0, Math.min(maxScroll(track), items[bounded].offsetLeft)), behavior });
      };
      const setControlState = (button, disabled) => {
        if (!button) return;
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
      };
      const updateDesktopControls = () => {
        const primary = desktopTracks[0];
        const previous = desktopNav?.querySelector('[data-cloudless-prev]');
        const next = desktopNav?.querySelector('[data-cloudless-next]');
        if (!primary || mobileView.matches) {
          setControlState(previous, true);
          setControlState(next, true);
          return;
        }
        const max = maxScroll(primary);
        setControlState(previous, primary.scrollLeft <= 1);
        setControlState(next, max <= 1 || primary.scrollLeft >= max - 1);
      };
      const applyDesktopIndex = (source) => {
        const index = nearestStartIndex(source);
        desktopTracks.forEach((track) => {
          if (track !== source) scrollDesktopToIndex(track, index);
        });
        updateDesktopControls();
      };
      const scheduleSyncRelease = (source, delay = 140) => {
        window.clearTimeout(releaseSyncTimer);
        releaseSyncTimer = window.setTimeout(() => {
          if (syncSource !== source) return;
          applyDesktopIndex(source);
          releaseSyncTimer = window.setTimeout(() => {
            if (syncSource === source) syncSource = null;
          }, 60);
        }, delay);
      };
      const syncDesktop = (source) => {
        if (mobileView.matches || (syncSource && syncSource !== source)) return;
        syncSource = source;
        cancelAnimationFrame(syncFrame);
        syncFrame = requestAnimationFrame(() => {
          applyDesktopIndex(source);
          scheduleSyncRelease(source);
        });
      };
      desktopTracks.forEach((track) => this.on(track, 'scroll', () => syncDesktop(track), { passive: true }));
      desktopTracks.forEach((track) => this.on(track, 'scrollend', () => {
        if (syncSource === track) scheduleSyncRelease(track, 0);
      }));
      const moveDesktop = (direction) => {
        const primary = desktopTracks[0];
        if (!primary || mobileView.matches) return;
        scrollDesktopToIndex(primary, nearestStartIndex(primary) + direction, 'smooth');
      };
      const moveMobile = (direction) => {
        if (!mobileView.matches) return;
        mobileTracks.forEach((track) => scrollToIndex(track, nearestIndex(track) + direction, true));
      };
      this.on(desktopNav?.querySelector('[data-cloudless-prev]'), 'click', () => moveDesktop(-1));
      this.on(desktopNav?.querySelector('[data-cloudless-next]'), 'click', () => moveDesktop(1));
      this.on(mobileNav?.querySelector('[data-cloudless-prev]'), 'click', () => moveMobile(-1));
      this.on(mobileNav?.querySelector('[data-cloudless-next]'), 'click', () => moveMobile(1));
      this.on(document, 'shopify:block:select', (event) => {
        const selected = event.target.closest?.('[data-cloudless-block-id]');
        if (!selected || !this.root.contains(selected)) return;
        const mode = mobileView.matches ? 'mobile' : 'desktop';
        const visibleCard = [...this.root.querySelectorAll(`[data-cloudless-testimonial-mode="${mode}"] [data-cloudless-block-id="${CSS.escape(selected.dataset.cloudlessBlockId)}"]`)].find((card) => card.offsetParent !== null);
        const track = visibleCard?.closest('[data-cloudless-testimonial-track]');
        if (!visibleCard || !track) return;
        scrollToIndex(track, cards(track).indexOf(visibleCard), mobileView.matches, event.detail?.load ? 'auto' : 'smooth');
        visibleCard.scrollIntoView({ behavior: event.detail?.load ? 'auto' : 'smooth', block: 'center', inline: 'center' });
      });
      this.on(window, 'resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
          tracks.forEach((track) => track.scrollTo({ left: 0, behavior: 'auto' }));
          updateDesktopControls();
        }, 250);
      }, { passive: true });
      updateDesktopControls();
      this.cleanup(() => {
        cancelAnimationFrame(syncFrame);
        window.clearTimeout(releaseSyncTimer);
        window.clearTimeout(resizeTimer);
      });
    }

    ingredients() {
      const buttons = [...this.root.querySelectorAll('[data-ingredient-button]')];
      const cards = [...this.root.querySelectorAll('[data-ingredient-card]')];
      const desktop = matchMedia('(min-width: 750px)');
      const initial = buttons.find((button) => button.classList.contains('is-active')) || buttons[buttons.length - 1];
      const initialId = initial?.dataset.ingredientButton || '';
      let editorPrevious = null;
      const currentId = () => buttons.find((button) => button.classList.contains('is-active'))?.dataset.ingredientButton || '';
      const select = (id) => {
        buttons.forEach((button) => {
          const active = button.dataset.ingredientButton === id;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-expanded', String(active));
        });
        cards.forEach((card) => {
          const active = card.dataset.ingredientCard === id;
          card.classList.toggle('is-active', active);
          card.setAttribute('aria-hidden', String(!active));
        });
      };
      buttons.forEach((button) => {
        this.on(button, 'click', () => {
          if (desktop.matches) return;
          const id = button.dataset.ingredientButton;
          select(currentId() === id ? '' : id);
        });
        this.on(button, 'mouseenter', () => {
          if (desktop.matches) select(button.dataset.ingredientButton);
        });
        this.on(button, 'keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          select(button.dataset.ingredientButton);
        });
      });
      this.on(document, 'shopify:block:select', (event) => {
        const block = event.target.closest?.('[data-ingredient-block]');
        if (!block || !this.root.contains(block)) return;
        editorPrevious = currentId();
        select(block.dataset.ingredientBlock);
        const card = [...cards].find((item) => item.dataset.ingredientCard === block.dataset.ingredientBlock && item.offsetParent !== null);
        card?.scrollIntoView({ behavior: event.detail?.load ? 'auto' : 'smooth', block: 'center' });
      });
      this.on(document, 'shopify:block:deselect', (event) => {
        const block = event.target.closest?.('[data-ingredient-block]');
        if (!block || !this.root.contains(block)) return;
        select(editorPrevious === null ? initialId : editorPrevious);
        editorPrevious = null;
      });
      this.on(desktop, 'change', () => {
        if (desktop.matches && !currentId()) select(initialId);
      });
      select(initialId);
    }

    comparison() {
      const scroller = this.root.querySelector('[data-cloudless-comparison-scroll]');
      const thumb = this.root.querySelector('[data-ct2-thumb]');
      const render = () => {
        if (!scroller || !thumb) return;
        const max = scroller.scrollWidth - scroller.clientWidth;
        const progress = max > 0 ? scroller.scrollLeft / max : 0;
        thumb.style.transform = `translateX(${progress * 24}px)`;
      };
      this.on(scroller, 'scroll', render, { passive: true });
      this.on(window, 'resize', render, { passive: true });
      this.on(document, 'shopify:block:select', (event) => {
        const row = event.target.closest?.('[data-cloudless-block-id]');
        if (!row || !this.root.contains(row)) return;
        row.scrollIntoView({ behavior: event.detail?.load ? 'auto' : 'smooth', block: 'center', inline: 'nearest' });
        render();
      });
      render();
    }

    hero() {
      this.on(this.root.querySelector('[data-cloudless-scroll-buybox]'), 'click', () => {
        const target = document.querySelector('.kaching-bundles, .cloudless-offers')
          || document.querySelector('.subscription-selector')
          || document.querySelector('.product-form-buttons')
          || document.querySelector('[data-product-form]')
          || document.querySelector('.product-form, .cloudless-product-form, [data-cloudless-product-form], product-form');
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    faq() {
      this.on(document, 'shopify:block:select', (event) => {
        const block = event.target.closest?.('[data-cloudless-faq-block]');
        if (!block || !this.root.contains(block)) return;
        const details = block.querySelector('details');
        if (details) details.open = true;
        block.scrollIntoView({ behavior: event.detail?.load ? 'auto' : 'smooth', block: 'center' });
      });
      this.on(document, 'shopify:block:deselect', (event) => {
        const block = event.target.closest?.('[data-cloudless-faq-block]');
        if (!block || !this.root.contains(block)) return;
        const details = block.querySelector('details');
        if (details) details.open = false;
      });
    }

    header() {
      const header = this.root.querySelector('.cloudless-header');
      const toggle = this.root.querySelector('[data-cloudless-menu-toggle]');
      const panel = this.root.querySelector('[data-cloudless-mobile-menu]');
      let lastY = window.scrollY;
      let hidden = false;
      const close = () => {
        panel?.classList.remove('is-open');
        toggle?.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('lock');
      };
      this.on(toggle, 'click', () => {
        const open = !panel?.classList.contains('is-open');
        panel?.classList.toggle('is-open', open);
        toggle?.setAttribute('aria-expanded', String(open));
        document.documentElement.classList.toggle('lock', open);
      });
      this.on(document, 'keydown', (event) => { if (event.key === 'Escape') close(); });
      this.on(window, 'scroll', () => {
        if (!header?.classList.contains('cloudless-header--hide-on-scroll')) return;
        const y = window.scrollY;
        const delta = y - lastY;
        if (Math.abs(delta) > 8 && !panel?.classList.contains('is-open')) {
          hidden = delta > 0 && y > 100;
          header.style.transform = hidden ? 'translateY(-110%)' : '';
        }
        lastY = y;
      }, { passive: true });
      this.on(window, 'mousemove', (event) => {
        if (hidden && event.clientY < 100) {
          hidden = false;
          header.style.transform = '';
        }
      });
    }
  }

  const mount = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-cloudless-controller]')) roots.push(scope);
    roots.push(...scope.querySelectorAll?.('[data-cloudless-controller]') || []);
    roots.forEach((root) => {
      if (!controllers.has(root)) controllers.set(root, new Controller(root));
    });
  };

  const unmount = (scope) => {
    const root = findSection(scope);
    if (root && controllers.has(root)) controllers.get(root).destroy();
    scope?.querySelectorAll?.('[data-cloudless-controller]').forEach((child) => controllers.get(child)?.destroy());
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
})();
